/**
 * The firm's default drafting templates: one per letter type and visa,
 * marked on a Firm Knowledge entry with the tag `default:<visa>` (or
 * `default:any` for an entry that serves any visa). Tags are the library's
 * own writable field, so marking a default is an ordinary entry edit.
 */
import { DRAFT_TYPE_LABELS, DraftType } from "@/api/types/draft";
import type { FirmLibraryEntry } from "@/features/firmKnowledge/types";
import { canonicalVisa } from "@/features/firmKnowledge/visaCatalog";
import { VISAVALUE } from "@/types/visa.type";

export const ANY_VISA = "any";
const DEFAULT_TAG_PREFIX = "default:";

/** "o-1a", or "any" for an entry that serves every visa. */
export const visaKey = (visa: string | null | undefined): string =>
  (visa ?? "").trim().toLowerCase() || ANY_VISA;

export const defaultTag = (visa: string | null | undefined): string =>
  `${DEFAULT_TAG_PREFIX}${visaKey(visa)}`;

/** "EB-2 NIW" -> "eb2niw". */
const compact = (visa: string): string =>
  visa.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Every visa's value and enum name, compacted, to its compacted value. */
const SPELLINGS: ReadonlyMap<string, string> = (() => {
  const visas = Object.entries(VISAVALUE).filter(([, value]) => value);
  const out = new Map(visas.map(([, v]) => [compact(v), compact(v)]));
  visas.forEach(([name, v]) => {
    if (!out.has(compact(name))) out.set(compact(name), compact(v));
  });
  return out;
})();

/** One key for a visa however it is spelled; a template version is its visa. */
const visaIdentity = (visa: string | null | undefined): string => {
  const raw = compact(visa ?? "");
  if (!raw) return ANY_VISA;
  const key = SPELLINGS.get(raw) ?? raw;
  return SPELLINGS.get(key.replace(/v\d+$/, "")) ?? key;
};

/**
 * Whether two spellings name one visa: the value ("EB-2 NIW"), the enum name
 * ("EB2_NIW") and a template version ("EB-2 NIW v2") all do.
 */
export const sameVisa = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => visaIdentity(a) === visaIdentity(b);

/** A `default:<visa>` tag that marks `visa` (null: any visa), however it spells the visa. */
const marksDefault = (tag: string, visa: string | null): boolean =>
  tag.startsWith(DEFAULT_TAG_PREFIX) &&
  (visa === null
    ? tag === defaultTag(null)
    : sameVisa(tag.slice(DEFAULT_TAG_PREFIX.length), visa));

export const draftTypeLabel = (draftType: string): string =>
  DRAFT_TYPE_LABELS[draftType as DraftType] ?? draftType;

/** An active entry with a letter type that the drafting agent copies or imitates. */
export const isDraftingTemplate = (entry: FirmLibraryEntry): boolean => {
  if (entry.status !== "active" || !entry.draft_type) return false;
  const category = entry.effective_category ?? entry.category ?? "sample";
  return category === "template" || category === "sample";
};

/** An entry's visas as Firm Knowledge names them: "EB-2 NIW" for either NIW version. */
const visasOf = (entry: FirmLibraryEntry): string[] => {
  const visas = entry.visas?.length
    ? entry.visas
    : entry.visa
    ? [entry.visa]
    : [];
  return [...new Set(visas.map((v) => canonicalVisa(v) ?? v))];
};

export type TemplateGroup = {
  key: string;
  draftType: string;
  label: string;
  /** Null for entries that serve any visa. */
  visa: string | null;
  entries: FirmLibraryEntry[];
  defaultId: string | null;
};

const isExhibitList = (entry: FirmLibraryEntry): boolean =>
  entry.draft_type === DraftType.EXHIBIT_LIST;

const isLetterTemplate = (entry: FirmLibraryEntry): boolean =>
  isDraftingTemplate(entry) && !isExhibitList(entry);

/** Letter templates by letter type, then visa; an entry serving two visas is in both. */
export const draftingGroups = (
  entries: readonly FirmLibraryEntry[]
): TemplateGroup[] => {
  const groups = new Map<string, TemplateGroup>();
  entries.filter(isLetterTemplate).forEach((entry) => {
    const draftType = entry.draft_type as string;
    const visas: (string | null)[] = visasOf(entry);
    (visas.length ? visas : [null]).forEach((visa) => {
      const key = `${draftType}|${visaKey(visa)}`;
      const group = groups.get(key) ?? {
        key,
        draftType,
        label: draftTypeLabel(draftType),
        visa,
        entries: [],
        defaultId: null
      };
      group.entries.push(entry);
      if (!group.defaultId && entry.tags.some((t) => marksDefault(t, visa)))
        group.defaultId = entry.id;
      groups.set(key, group);
    });
  });
  return [...groups.values()].sort(
    (a, b) =>
      a.label.localeCompare(b.label) ||
      (a.visa === null
        ? -1
        : b.visa === null
        ? 1
        : a.visa.localeCompare(b.visa))
  );
};

/**
 * The firm's Word exhibit lists, whatever visa they came from: one default
 * (`default:any`) styles every exhibit list CaseBlink writes, as a list's
 * letterhead and layout are the firm's, not the visa's. None: CaseBlink's.
 */
export const exhibitListGroup = (
  entries: readonly FirmLibraryEntry[]
): TemplateGroup => {
  const lists = entries.filter(
    (e) => isDraftingTemplate(e) && isExhibitList(e) && e.authorable
  );
  return {
    key: `${DraftType.EXHIBIT_LIST}|${ANY_VISA}`,
    draftType: DraftType.EXHIBIT_LIST,
    label: draftTypeLabel(DraftType.EXHIBIT_LIST),
    visa: null,
    entries: lists,
    defaultId:
      lists.find((e) => e.tags.some((t) => marksDefault(t, null)))?.id ?? null
  };
};

export type TagEdit = { id: string; tags: string[] };

/** The tag edits that make `entryId` the group's default and unmark the others. */
export const markDefaultEdits = (
  group: TemplateGroup,
  entryId: string | null
): TagEdit[] => {
  const tag = defaultTag(group.visa);
  const marks = (t: string) => marksDefault(t, group.visa);
  return group.entries.flatMap((entry) => {
    const has = entry.tags.some(marks);
    if (entry.id === entryId)
      return has ? [] : [{ id: entry.id, tags: [...entry.tags, tag] }];
    return has
      ? [{ id: entry.id, tags: entry.tags.filter((t) => !marks(t)) }]
      : [];
  });
};

export type DefaultTemplate = {
  draftType: string;
  label: string;
  name: string;
  entryId: string;
};

/** The defaults a case of `visa` drafts from: the visa's own first, then any-visa ones. */
export const defaultTemplatesFor = (
  entries: readonly FirmLibraryEntry[],
  visa: string
): DefaultTemplate[] => {
  const picked = new Map<string, DefaultTemplate>();
  const groups = draftingGroups(entries);
  const take = (group: TemplateGroup) => {
    if (!group.defaultId || picked.has(group.draftType)) return;
    const entry = group.entries.find((e) => e.id === group.defaultId);
    if (entry)
      picked.set(group.draftType, {
        draftType: group.draftType,
        label: group.label,
        name: entry.name,
        entryId: entry.id
      });
  };
  if (visaKey(visa) !== ANY_VISA)
    groups.filter((g) => g.visa && sameVisa(g.visa, visa)).forEach(take);
  groups.filter((g) => g.visa === null).forEach(take);
  return [...picked.values()].sort((a, b) => a.label.localeCompare(b.label));
};
